import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StatWidget } from '@/components/widgets/StatWidget';
import { clientsAssetsApi } from '@/api/services/clientsAssets';
import { formatNumber } from '@/utils/format';
import { ApiError } from '@/api/client/abpError';
import { useToast } from '@/components/common/toast';
import { BreakdownCard, type BreakdownTable } from './BreakdownCard';
import type {
  AssetTypeConnectorTotalDto,
  ClientsAssetsSummaryDto,
  ConnectorCurrencyTotalDto,
  ConnectorTotalDto,
  ProviderTotalDto,
} from '@/api/types/clientsAssets';

type Panel = 'equity' | 'dw' | 'profit';

interface SummaryTabProps {
  referenceDate: string;
  isLive: boolean;
  // Bumped by the parent Fetch button to force a reload.
  reloadKey: number;
}

export function SummaryTab({ referenceDate, isLive, reloadKey }: SummaryTabProps) {
  const toast = useToast();
  const [open, setOpen] = useState<Panel | null>(null);

  const query = useQuery({
    queryKey: ['clients-assets', 'summary', referenceDate, isLive, reloadKey],
    queryFn: () => clientsAssetsApi.getSummary({ referenceDate, isLive }),
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      const msg = query.error instanceof ApiError ? query.error.message : 'Failed to load the summary.';
      toast.error(msg || 'Failed to load the summary.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  const s: ClientsAssetsSummaryDto | undefined = query.data;
  const toggle = (p: Panel) => setOpen((cur) => (cur === p ? null : p));

  return (
    <div>
      <div className="row g-2">
        <div className="col-md-4">
          <StatWidget
            label="Equity"
            value={formatNumber(s?.equityTotal ?? 0, 2)}
            icon="fa-solid fa-square-poll-vertical"
            theme="primary"
            onClick={() => toggle('equity')}
            active={open === 'equity'}
          />
        </div>
        <div className="col-md-4">
          <StatWidget
            label="Deposits/Withdrawals"
            value={formatNumber(s?.depositsWithdrawalsTotal ?? 0, 2)}
            icon="fa-solid fa-money-bill-transfer"
            theme="info"
            onClick={() => toggle('dw')}
            active={open === 'dw'}
          />
        </div>
        <div className="col-md-4">
          <StatWidget
            label="Profit"
            value={formatNumber(s?.profitTotal ?? 0, 2)}
            icon="fa-solid fa-chart-line"
            theme="neutral"
            onClick={() => toggle('profit')}
            active={open === 'profit'}
          />
        </div>
      </div>

      {query.isLoading && <div className="text-muted mt-3">Loading…</div>}

      {open === 'equity' && s && (
        <BreakdownCard
          tables={[
            {
              title: 'Equity By Connector',
              columns: [
                { header: 'Connector', cell: (r) => (r as ConnectorTotalDto).connector },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as ConnectorTotalDto).total, 2) },
              ],
              rows: s.equityByConnector,
            },
            {
              title: 'Equity By Connector And Assets Type',
              columns: [
                { header: 'Assets Type', cell: (r) => (r as AssetTypeConnectorTotalDto).assetType },
                { header: 'Connector', cell: (r) => (r as AssetTypeConnectorTotalDto).connector },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as AssetTypeConnectorTotalDto).total, 2) },
              ],
              rows: s.equityByAssetType,
            },
          ] as BreakdownTable<unknown>[]}
        />
      )}

      {open === 'dw' && s && (
        <BreakdownCard
          tables={[
            {
              title: 'D/W By Connector',
              columns: [
                { header: 'Connector', cell: (r) => (r as ConnectorTotalDto).connector },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as ConnectorTotalDto).total, 2) },
              ],
              rows: s.depositsWithdrawalsByConnector,
            },
            {
              title: 'D/W By Providers',
              columns: [
                { header: 'Provider', cell: (r) => (r as ProviderTotalDto).provider },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as ProviderTotalDto).total, 2) },
              ],
              rows: s.depositsWithdrawalsByProvider,
            },
          ] as BreakdownTable<unknown>[]}
        />
      )}

      {open === 'profit' && s && (
        <BreakdownCard
          tables={[
            {
              title: 'Profit By Connector',
              columns: [
                { header: 'Connector', cell: (r) => (r as ConnectorTotalDto).connector },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as ConnectorTotalDto).total, 2) },
              ],
              rows: s.profitByConnector,
            },
            {
              title: 'Profit By Account Currencies',
              columns: [
                { header: 'Connector', cell: (r) => (r as ConnectorCurrencyTotalDto).connector },
                { header: 'Acc Currency', cell: (r) => (r as ConnectorCurrencyTotalDto).currency },
                { header: 'Total', className: 'text-end', cell: (r) => formatNumber((r as ConnectorCurrencyTotalDto).total, 2) },
              ],
              rows: s.profitByAccountCurrency,
            },
          ] as BreakdownTable<unknown>[]}
        />
      )}
    </div>
  );
}
